import {
  renderPriority,
  useCustomFilters,
} from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDisconnectionExport, useDisconnectionRequests } from "../../hooks";
import { DisconnectionFilters, DisconnectionRequest } from "../../types";
import { DisconnectionFilterPanel } from "../filters";

export const CurrentList: React.FC = () => {
  const CURRENCY = useCurrency();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  const columns: TableColumn<DisconnectionRequest>[] = useMemo(
    () => [
      { key: "requestId", header: "Request ID", type: "text" },
      // { key: "customerName", header: "Customer", type: "text" },
      // { key: "accountNumber", header: "Account Number", type: "text" },
      {
        key: "customerName",
        header: "Consumer",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value ?? "NA"}</p>
            <p className="text-xs text-muted-foreground">
              {row.accountNumber ?? "NA"}
            </p>
          </div>
        ),
      },
      { key: "name", header: "Reason", type: "text" },
      { key: "dateRequested", header: " Preffered  Date", type: "text" },
      {
        key: "status",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: (value: string) => {
            if (value === "pending") return "secondary";
            if (value === "scheduled") return "default";
            if (value === "completed") return "outline";
            if (value === "cancelled") return "destructive";
            return "default";
          },
        },
      },
      {
        key: "createdDate",
        header: "Created On",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value ?? "NA"}</p>
            <p className="text-xs text-muted-foreground">
              {row.createdUserRemoteName ?? "NA"}
            </p>
          </div>
        ),
      },

      {
        key: "outstandingBalance",
        header: "Outstanding",
        type: "custom",
        render: (value) => `${CURRENCY} ${value}`,
      },
      {
        key: "priorityLevelDisplay",
        header: "Priority",
        type: "custom",
        render: (value) => renderPriority(value),
      },
    ],
    [renderPriority]
  );

  const actions: TableAction<DisconnectionRequest>[] = useMemo(
    () => [
      {
        label: "View",
        onClick: (item) => navigate(`/cx/disconnections/current/${item.id}`),
      },
      
    ],
    [navigate]
  );

  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
        name: "name",
      },
      defaults: {
        page: 1,
        limit: 50,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
        name: "",
      },
      parsers: {
        status: (value: string) => value.split(",").map(Number),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: number[]) => value.join(","),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const exportFilters: DisconnectionFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: filters.search_data,
      activeTab: "Current",
      requestType: "Disconnect Permanent",
      name: filters.name,
      status: filters.status,
      // Note: No page and limit for export to get all data
    };
  }, [filters, remoteUtilityId]);

  const { data } = useDisconnectionRequests({
    page: filters.page,
    limit: PAGE_SIZE,
    searchData: filters.search_data,
    activeTab: "Current",
    requestType: "Disconnect Permanent",
    remoteUtilityId: Number(remoteUtilityId),
    name: filters.name,
    status: filters.status,
  });

  const { exportCSV, isExporting } = useDisconnectionExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };
  

  const transformedData = useMemo(
    () =>
      data?.results?.map((item: DisconnectionRequest) => ({
        id: item.id,
        requestId: item.requestNo,
        customerName: `${item.consumer.firstName} ${item.consumer.lastName}`,
        accountNumber: item.consumer.consumerNo,
        serviceAddress: item.consumer?.serviceAddress,
        type: item.requestTypeDisplay,
        dateRequested: item.requestDate,
        status: item.statusDisplay,
        outstandingBalance: item.outstandingBalance,
        totalOutstanding: item.additionalData?.totalOutstanding,
        priorityLevelDisplay: item.priorityLevelDisplay,
        createdDate: item.createdDate,
        createdUserRemoteName: item.createdUserRemoteName,
        name: item?.utilitySupportRequest?.name,
        category: item.supportRequestType,
        subCategory: item.supportRequestSubType,
      })) || [],
    [data?.results]
  );

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalPages, totalItems]
  );
  const currentTab = 2;

  return (
    <GenericTable
      data={transformedData as unknown as DisconnectionRequest[]}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by name, email, or ID..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      pagination={pagination}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      onExport={handleExport}
      exportLoading={isExporting}
      filterPanel={
        <DisconnectionFilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          tab={currentTab}
        />
      }
    />
  );
};
